import React, { useState, useEffect } from 'react';
import { locationService } from '../../api/apiService';
import './LocationManagement.css';

const LocationManagement = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    city: '',
    short: '',
    parent_id: '' // Optional parent_id
  });

  // Load locations on component mount
  useEffect(() => {
    loadLocations();
  }, []);

  const loadLocations = async () => {
    try {
      setLoading(true);
      const response = await locationService.getAllLocations();
      setLocations(response.data.locations || []);
      setError('');
    } catch (err) {
      console.error('Error loading locations:', err);
      setError('Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      city: '',
      short: '',
      parent_id: ''
    });
    setEditingLocation(null);
    setShowAddForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name.trim() || !formData.city.trim() || !formData.short.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      
      if (editingLocation) {
        // Update existing location
        await locationService.updateLocation(editingLocation.id, formData);
        setSuccess('Location updated successfully!');
      } else {
        // Create new location
        await locationService.createLocation(formData);
        setSuccess('Location created successfully!');
      }
      
      resetForm();
      loadLocations();
      setError('');
    } catch (err) {
      console.error('Error saving location:', err);
      setError(err.response?.data?.message || 'Failed to save location');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (location) => {
    setFormData({
      name: location.name || '',
      city: location.city || '',
      short: location.short || '',
      parent_id: location.parent_id || ''
    });
    setEditingLocation(location);
    setShowAddForm(true);
  };

  const handleDelete = async (location) => {
    if (!window.confirm(`Are you sure you want to delete location "${location.name}"?`)) {
      return;
    }

    try {
      setLoading(true);
      await locationService.deleteLocation(location.id);
      setSuccess('Location deleted successfully!');
      loadLocations();
      setError('');
    } catch (err) {
      console.error('Error deleting location:', err);
      setError(err.response?.data?.message || 'Failed to delete location');
    } finally {
      setLoading(false);
    }
  };

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  return (
    <div className="location-management">
      <div className="location-header">
        <h1>Location Management</h1>
        <button 
          className="btn btn-primary"
          onClick={() => {
            setShowAddForm(true);
            clearMessages();
          }}
          disabled={loading}
        >
          Add New Location
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="alert alert-danger">
          {error}
          <button className="close-btn" onClick={clearMessages}>×</button>
        </div>
      )}
      
      {success && (
        <div className="alert alert-success">
          {success}
          <button className="close-btn" onClick={clearMessages}>×</button>
        </div>
      )}

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="location-form-container">
          <div className="location-form">
            <h2>{editingLocation ? 'Edit Location' : 'Add New Location'}</h2>
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">
                  Location Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., Tarpenring 4, 22419 Hamburg"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="city">
                  City <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="e.g., Hamburg"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="short">
                  Short Code <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="short"
                  name="short"
                  value={formData.short}
                  onChange={handleInputChange}
                  placeholder="e.g., TR4 or Copywriting"
                  maxLength="50"
                  style={{ textTransform: 'uppercase' }}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="parent_id">Parent ID (Optional)</label>
                <input
                  type="number"
                  id="parent_id"
                  name="parent_id"
                  value={formData.parent_id}
                  onChange={handleInputChange}
                  min="1"
                  placeholder="Leave empty if not needed"
                />
                <small style={{ color: '#6c757d', fontSize: '12px' }}>
                  Only needed if location belongs to a specific company
                </small>
              </div>

              <div className="form-actions">
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : (editingLocation ? 'Update Location' : 'Create Location')}
                </button>
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={resetForm}
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Locations List */}
      <div className="locations-list">
        <h2>Existing Locations ({locations.length})</h2>
        
        {loading && !showAddForm && (
          <div className="loading">Loading locations...</div>
        )}

        {!loading && locations.length === 0 && (
          <div className="no-locations">
            <p>No locations found. Add your first location above!</p>
          </div>
        )}

        {locations.length > 0 && (
          <div className="locations-table">
            <table>
              <thead>
                <tr>
                  <th>Short Code</th>
                  <th>Name</th>
                  <th>City</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {locations.map((location) => (
                  <tr key={location.id}>
                    <td>
                      <span className="location-short">{location.short}</span>
                    </td>
                    <td>{location.name}</td>
                    <td>{location.city}</td>
                    <td>
                      {location.created_at ? 
                        new Date(location.created_at).toLocaleDateString() : 
                        'N/A'
                      }
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-sm btn-edit"
                          onClick={() => handleEdit(location)}
                          disabled={loading}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-delete"
                          onClick={() => handleDelete(location)}
                          disabled={loading}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationManagement;
